import { useEffect, useState } from 'react'
import { NotificationLogDto, NotificationLogStatus } from '@ivy-monorepo/shared'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import { adminService } from '../../services/admin.service'

const STATUS_OPTIONS: { value: NotificationLogStatus | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: NotificationLogStatus.Success, label: 'Success' },
  { value: NotificationLogStatus.Failure, label: 'Failure' },
  { value: NotificationLogStatus.Skipped, label: 'Skipped' },
]

const NotificationsLogTab = () => {
  const [logs, setLogs] = useState<NotificationLogDto[]>([])
  const [page, setPage] = useState(1)
  const [pageSize] = useState(25)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [status, setStatus] = useState<NotificationLogStatus | ''>('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 0 })
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setPage(1)
  }, [status, debouncedSearch])

  useEffect(() => {
    let cancelled = false
    const fetchLogs = async () => {
      setIsLoading(true)
      try {
        const response = await adminService.getNotificationLogs(
          page,
          pageSize,
          debouncedSearch,
          sortOrder,
          status || undefined,
        )
        if (cancelled) return
        setLogs(response.notificationLogs)
        setPagination(response.pagination)
      } catch (err) {
        console.log('Failed to fetch notification logs', err)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchLogs()
    return () => {
      cancelled = true
    }
  }, [page, pageSize, sortOrder, status, debouncedSearch])

  const handleSortChange = (_sortBy: string, order: string) => {
    setSortOrder(order === 'asc' ? 'asc' : 'desc')
    setPage(1)
  }

  return (
    <div className="notifications-log-tab">
      <p style={{ marginBottom: '16px', color: '#555' }}>
        Every push send attempt — successful, failed, or skipped — across presets, my places, venue alerts,
        test push, blasts and badge updates. Testing tools for the habit loop live on the Test push tab.
      </p>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Search message, user, preset, reason…"
          style={{ flex: 1, minWidth: '220px', padding: '8px 10px' }}
        />
        <select
          value={status}
          onChange={event => setStatus(event.target.value as NotificationLogStatus | '')}
          style={{ padding: '8px 10px' }}
        >
          {STATUS_OPTIONS.map(option => (
            <option key={option.value || 'all'} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={setPage}
        />
      </div>

      <div className="users-table-container">
        <div className="table-wrapper">
          <Table
            id="notification-logs"
            name="Notification log"
            keyField="publicId"
            initialSortColumn="createdAt"
            initialSortOrder={sortOrder}
            columns={[
              { label: 'Sent at', accessor: 'createdAt', type: 'datetime' },
              { label: 'Status', accessor: 'status', type: 'enum', enumName: 'NotificationLogStatus' },
              { label: 'Channel', accessor: 'channel', type: 'enum', enumName: 'NotificationLogChannel' },
              { label: 'Type', accessor: 'notificationType', type: 'enum', enumName: 'NotificationType' },
              { label: 'Recipient', accessor: 'recipientFullName', type: 'text' as any },
              { label: 'Source', accessor: 'source', type: 'text' as any },
              { label: 'Preset', accessor: 'presetPublicId', type: 'text' as any },
              { label: 'Message', accessor: 'message', type: 'text' as any },
              { label: 'Failure reason', accessor: 'failureReason', type: 'text' as any },
            ]}
            sourceData={logs}
            isPropertyBarVisible={false}
            filterTerms=""
            onSortChange={handleSortChange}
            disableGlobalSearch={true}
            disableClientSort={true}
          />
        </div>
      </div>
      {isLoading && logs.length === 0 ? <p style={{ color: '#777', marginTop: '12px' }}>Loading…</p> : null}
      {!isLoading && logs.length === 0 ? <p style={{ color: '#777', marginTop: '12px' }}>No notification log entries found.</p> : null}
    </div>
  )
}

export default NotificationsLogTab
